import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Rocket, Users, Hourglass, ShieldCheck, CreditCard } from "lucide-react";
import { Stat, Section, Loading, Empty, Pill, RawJson, useFetch } from "./_shared";

function Counts({ obj }) {
  const flat = [];
  for (const [k, v] of Object.entries(obj || {})) {
    if (v && typeof v === "object") {
      for (const [k2, v2] of Object.entries(v)) flat.push([`${k}.${k2}`, v2]);
    } else flat.push([k, v]);
  }
  if (flat.length === 0) return <Empty text="Keine Daten." />;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {flat.map(([k, v]) => <Stat key={k} label={k} value={String(v)} />)}
    </div>
  );
}

function OnDemandTool({ title, icon, path, testId, renderResult }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data: r } = await api.get(path); setData(r); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Section title={title} testId={testId}
      action={<Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={run} data-testid={`${testId}-run`}>{busy ? "Lädt…" : (data ? "Aktualisieren" : "Laden")}</Button>}>
      {!data ? <p className="text-sm text-muted-foreground">Auf „Laden“ tippen, um die Daten abzurufen.</p> : (
        <div className="space-y-2">{renderResult(data)}<RawJson data={data} /></div>
      )}
    </Section>
  );
}

export default function PreLaunch() {
  const { data: founder, loading, reload: reloadFounder } = useFetch("/admin/pre-launch/founder-50-status");
  const { data: ic } = useFetch("/admin/ic-waitlist/overview");
  const { data: digest } = useFetch("/admin/analytics/digest-ab?days=90");
  const [busy, setBusy] = useState(false);

  if (loading) return <Loading />;
  const founders = founder?.founders || []; const pending = founder?.pending || [];
  const digestRows = digest?.rows || [];
  const icRows = ic?.rows || [];

  const promoteFounder = async () => {
    if (!window.confirm("Founder-50-Kohorte jetzt promoten? (idempotent, sicher wiederholbar)")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/pre-launch/promote-founder-50", { confirm: "FOUNDER 50 OK" }); toast.success(`Promote fertig · ${data.summary?.total_promoted ?? 0} promotet`); await reloadFounder(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const backfillVerify = async () => {
    if (!window.confirm("Alle echten Coaches als verifiziert markieren (Backfill)?")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/pre-launch/verification-backfill", { all_real_coaches: true, confirm: "BACKFILL VERIFY" }); toast.success(`Backfill: ${data.updated ?? data.modified ?? 0} aktualisiert`); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Admin" title="Pre-Launch" subtitle="Founder-Kohorte, Wartelisten, Audits & Diagnostik." />

      <Section title="Founder-50" testId="admin-pl-founder"
        action={<Button size="sm" className="gap-1.5 rounded-full" disabled={busy} onClick={promoteFounder} data-testid="admin-pl-founder-promote"><Rocket className="h-4 w-4" /> Kohorte promoten</Button>}>
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Founder-Konten" value={founder?.count ?? founders.length} tone="good" testId="admin-pl-founder-count" />
          <Stat label="Ausstehend (Signup)" value={founder?.pending_count ?? pending.length} tone={pending.length ? "warn" : "default"} />
          <Stat label="Coaches" value={founders.filter((f) => f.role === "coach").length} />
        </div>
      </Section>

      <Section title="Inner-Circle-Warteliste" testId="admin-pl-icwaitlist">
        <div className="mb-3 grid grid-cols-2 gap-3">
          <Stat label="Wartende gesamt" value={ic?.total_waiters ?? 0} testId="admin-pl-ic-total" />
          <Stat label="Coaches mit Warteliste" value={ic?.distinct_coaches ?? 0} />
        </div>
        {icRows.length === 0 ? <Empty text="Keine Einträge." /> : (
          <div className="space-y-1.5">
            {icRows.slice(0, 20).map((r) => (
              <div key={r.coach_id} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-background p-2.5 text-sm">
                <span className="truncate text-foreground">{r.coach_name || r.coach_id} {r.inner_circle_published ? <Pill tone="good">live</Pill> : <Pill tone="warn">unveröffentlicht</Pill>}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{r.count} wartend</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Digest A/B-Test (90T)" testId="admin-pl-digest">
        {digestRows.length === 0 ? <Empty text="Noch keine Digest-Sends." /> : (
          <div className="space-y-2">
            {digestRows.map((r) => (
              <div key={r.variant} className="rounded-xl border border-border bg-background p-3">
                <p className="text-sm font-medium text-foreground">Variante {r.variant} — {r.label} {digest?.winner === r.variant && <Pill tone="good">Sieger</Pill>}</p>
                <p className="text-xs text-muted-foreground">{r.sends} Sends · {r.opens} Öffnungen ({r.open_rate_pct}%) · {r.clicks} Klicks ({r.click_rate_pct}%) · {r.bounces} Bounces</p>
              </div>
            ))}
            {digest?.winner && <p className="text-xs text-muted-foreground">Lift B vs A: {digest.lift_pct}%</p>}
          </div>
        )}
      </Section>

      <OnDemandTool title="Aktive Nutzer" icon={Users} path="/admin/pre-launch/active-users" testId="admin-pl-activeusers"
        renderResult={(d) => (
          <>
            <div className="mb-2 grid grid-cols-3 gap-3">
              <Stat label="Nutzer gesamt" value={d.total ?? 0} />
              <Stat label="Founders" value={d.founders_count ?? 0} tone="good" />
              <Stat label="Rollen" value={Object.keys(d.by_role || {}).length} />
            </div>
            <p className="mb-1 text-xs font-semibold text-muted-foreground">Nach Rolle</p>
            <Counts obj={d.by_role} />
            <p className="mb-1 mt-2 text-xs font-semibold text-muted-foreground">Nach Tier</p>
            <Counts obj={d.by_tier} />
          </>
        )} />

      <Section title="Verifizierungs-Audit" testId="admin-pl-verify">
        <OnDemandTool title="Coach-Verifizierungsstatus" icon={ShieldCheck} path="/admin/pre-launch/verification-audit" testId="admin-pl-verify-audit"
          renderResult={(d) => (<><Stat label="Coaches" value={d.count ?? 0} /><div className="mt-2"><Counts obj={d.by_status} /></div></>)} />
        <div className="mt-2">
          <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy} onClick={backfillVerify} data-testid="admin-pl-verify-backfill"><ShieldCheck className="h-4 w-4" /> Alle echten Coaches verifizieren</Button>
        </div>
      </Section>

      <OnDemandTool title="Abo-Audit" icon={Hourglass} path="/admin/pre-launch/subscription-audit" testId="admin-pl-subaudit"
        renderResult={(d) => <Counts obj={d.buckets || d.by_source || d} />} />

      <OnDemandTool title="Zahlungs-Probe (Stripe / RevenueCat)" icon={CreditCard} path="/admin/pre-launch/payments-probe" testId="admin-pl-payments"
        renderResult={(d) => (
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded-xl border border-border bg-background p-3">
              <p className="text-sm font-medium text-foreground">Stripe <Pill tone={d.stripe?.mode === "LIVE" ? "good" : (d.stripe?.error ? "bad" : "warn")}>{d.stripe?.mode || (d.stripe?.error ? "Fehler" : "—")}</Pill></p>
              <p className="text-xs text-muted-foreground">{d.stripe?.error || `${d.stripe?.country || "—"} · charges ${d.stripe?.charges_enabled ? "✓" : "✗"} · payouts ${d.stripe?.payouts_enabled ? "✓" : "✗"}`}</p>
            </div>
            <div className="rounded-xl border border-border bg-background p-3">
              <p className="text-sm font-medium text-foreground">RevenueCat <Pill tone={d.revenuecat?.reachable ? "good" : "bad"}>{d.revenuecat?.reachable ? "erreichbar" : "Fehler"}</Pill></p>
              <p className="text-xs text-muted-foreground">{d.revenuecat?.error || `${d.revenuecat?.products_count ?? 0} Produkte · ${d.revenuecat?.offerings_count ?? 0} Offerings`}</p>
            </div>
          </div>
        )} />
    </div>
  );
}
